import React, { useState, useEffect } from 'react';
import { DataLayer } from '../lib/dataLayer';
import { Database } from '../types/database';
import { useAuth } from '../contexts/AuthContext';

type Lesson = Database['public']['Tables']['lessons']['Row'];
type UserLessonProgress = Database['public']['Tables']['user_lesson_progress']['Row'];

interface LessonListProps {
  onSelectLesson: (lesson: Lesson) => void;
  onCreateLesson?: () => void;
}

export default function LessonList({ onSelectLesson, onCreateLesson }: LessonListProps) {
  const { user, profile } = useAuth();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [userProgress, setUserProgress] = useState<Map<string, UserLessonProgress>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);

  useEffect(() => {
    loadLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.is_admin]);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const allLessons = await DataLayer.getLessons(profile?.is_admin || false);
      setLessons(allLessons);

      // Load user progress for each lesson
      if (user) {
        const progressMap = new Map<string, UserLessonProgress>();
        for (const lesson of allLessons) {
          const progress = await DataLayer.getUserLessonProgress(user.id, lesson.id);
          if (progress) {
            progressMap.set(lesson.id, progress);
          }
        }
        setUserProgress(progressMap);
      }
    } catch (error) {
      console.error('Failed to load lessons:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (lessonId: string) => {
    const progress = userProgress.get(lessonId);
    if (!progress) return null;

    switch (progress.status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Completed
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            In Progress
          </span>
        );
      default:
        return null;
    }
  };

  const filteredLessons = lessons.filter(lesson => {
    if (selectedLanguage && lesson.language !== selectedLanguage) return false;
    if (selectedLevel && lesson.level !== selectedLevel) return false;
    return true;
  });

  const languages = Array.from(new Set(lessons.map(l => l.language)));
  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading lessons...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {languages.map(lang => (
            <option key={lang} value={lang}>
              {lang === 'es' ? 'Spanish' : lang.toUpperCase()}
            </option>
          ))}
        </select>

        <select
          value={selectedLevel || ''}
          onChange={(e) => setSelectedLevel(e.target.value || null)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Levels</option>
          {levels.map(level => (
            <option key={level} value={level}>{level}</option>
          ))}
        </select>

        {profile?.is_admin && onCreateLesson && (
          <button
            onClick={onCreateLesson}
            className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Create Lesson
          </button>
        )}
      </div>

      {/* Lesson List */}
      {filteredLessons.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No lessons found for the selected filters.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredLessons.map((lesson) => (
            <div
              key={lesson.id}
              onClick={() => onSelectLesson(lesson)}
              className="cursor-pointer p-6 bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{lesson.title}</h3>
                {!lesson.is_published && profile?.is_admin && (
                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    Draft
                  </span>
                )}
              </div>
              
              {lesson.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {lesson.description}
                </p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  {lesson.level && (
                    <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
                      {lesson.level}
                    </span>
                  )}
                  {lesson.exercise && (
                    <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded">
                      {lesson.exercise}
                    </span>
                  )}
                </div>
                {getStatusBadge(lesson.id)}
              </div>

              {lesson.tags && lesson.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {lesson.tags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-gray-50 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {lesson.tags.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{lesson.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}